/**
 * CombineDocsModal — pick documents to combine with the open one, in order.
 *
 * Process Docs viewer → "Combine documents". The open document is listed first; the
 * user ticks others from the list (search by name), puts them in order with ↑ / ↓, and
 * "Combine" makes ONE new document of all their pages in that order. The originals are
 * replaced (utils/pageEdit.ts) — the footer warns that this cannot be undone.
 *
 * Only documents still To File / Date Verified are offered: a filed document carries
 * filing and bookkeeping work that a combined file would not.
 * Keyboard: Tab through the order list, the search and the list; Enter / Space ticks;
 * Esc closes.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiArrowUp, FiArrowDown, FiX, FiSearch } from 'react-icons/fi';
import './UploadDocsPage.css';   // the ud-split-* dialog styles, shared with the split screen
import './CombineDocsModal.css';

export interface CombineCandidate {
  document_id: number;
  filename: string;
  date_of_document?: string | null;
  page_count?: number | null;
}

interface CombineDocsModalProps {
  current: CombineCandidate;
  candidates: CombineCandidate[];
  busy?: boolean;
  /** Document ids in the order their pages should appear (the open one included). */
  onConfirm: (orderedIds: number[]) => void;
  onClose: () => void;
}

const label = (d: CombineCandidate) =>
  `${d.filename}${d.page_count ? ` · ${d.page_count} pg` : ''}${d.date_of_document ? ` · ${String(d.date_of_document).slice(0, 10)}` : ''}`;

const CombineDocsModal: React.FC<CombineDocsModalProps> = ({ current, candidates, busy, onConfirm, onClose }) => {
  const [order, setOrder] = useState<number[]>([current.document_id]);
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement | null>(null);
  const byId = useMemo(() => {
    const m = new Map<number, CombineCandidate>();
    [current, ...candidates].forEach(d => m.set(d.document_id, d));
    return m;
  }, [current, candidates]);

  useEffect(() => { searchRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return candidates.filter(d => !q || d.filename.toLowerCase().includes(q)).slice(0, 200);
  }, [candidates, search]);

  const toggle = (id: number) => setOrder(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  const move = (idx: number, by: -1 | 1) => setOrder(prev => {
    const next = prev.slice();
    const j = idx + by;
    if (j < 0 || j >= next.length) return prev;
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });
  const pages = order.reduce((n, id) => n + (Number(byId.get(id)?.page_count) || 0), 0);

  return (
    <div className="ud-split-overlay" onClick={() => { if (!busy) onClose(); }}>
      <div className="ud-split-modal cdm-modal" onClick={e => e.stopPropagation()}
        role="dialog" aria-modal="true" aria-label="Combine documents">
        <div className="ud-split-header">
          <div>
            <h3 className="ud-split-title">Combine documents</h3>
            <p className="ud-split-sub">
              Tick the documents to add after <strong>{current.filename}</strong>, then put them in order.
              Their pages become one new document. Only documents not filed yet are listed.
            </p>
          </div>
        </div>

        <div className="ud-split-body cdm-body">
          <div className="cdm-section-label">Order of pages in the new document</div>
          <ol className="cdm-order">
            {order.map((id, idx) => {
              const d = byId.get(id);
              if (!d) return null;
              return (
                <li key={id} className="cdm-order-item">
                  <span className="cdm-order-no">{idx + 1}</span>
                  <span className="cdm-order-name" title={d.filename}>{label(d)}</span>
                  <button type="button" className="cdm-icon-btn" disabled={idx === 0 || busy}
                    onClick={() => move(idx, -1)} title="Move up" aria-label={`Move ${d.filename} up`}><FiArrowUp size={14} /></button>
                  <button type="button" className="cdm-icon-btn" disabled={idx === order.length - 1 || busy}
                    onClick={() => move(idx, 1)} title="Move down" aria-label={`Move ${d.filename} down`}><FiArrowDown size={14} /></button>
                  {id !== current.document_id && (
                    <button type="button" className="cdm-icon-btn" disabled={busy}
                      onClick={() => toggle(id)} title="Take out" aria-label={`Take ${d.filename} out`}><FiX size={14} /></button>
                  )}
                </li>
              );
            })}
          </ol>

          <div className="cdm-section-label">Add documents</div>
          <div className="cdm-search">
            <FiSearch size={13} />
            <input ref={searchRef} type="text" placeholder="Search by file name…" value={search}
              onChange={e => setSearch(e.target.value)} />
            {search && (
              <button type="button" className="cdm-icon-btn" onClick={() => setSearch('')} aria-label="Clear search"><FiX size={13} /></button>
            )}
          </div>
          <div className="cdm-list" role="listbox" aria-multiselectable="true" aria-label="Documents to add">
            {shown.length === 0 && <div className="cdm-empty">No other documents to file match.</div>}
            {shown.map(d => {
              const on = order.includes(d.document_id);
              return (
                <label key={d.document_id} className={`cdm-row ${on ? 'on' : ''}`} role="option" aria-selected={on}>
                  <input type="checkbox" checked={on} disabled={busy} onChange={() => toggle(d.document_id)} />
                  <span className="cdm-row-name" title={d.filename}>{label(d)}</span>
                </label>
              );
            })}
          </div>
        </div>

        <div className="ud-split-footer">
          <span className="ud-split-summary">
            {order.length} {order.length === 1 ? 'document' : 'documents'}{pages ? ` · ${pages} pages` : ''} → <strong>1 document</strong>
          </span>
          {order.length > 1 && (
            <span className="ud-split-warning" role="alert">
              ⚠ These {order.length} documents will be replaced by the combined one. This cannot be undone.
            </span>
          )}
          <div className="ud-split-actions">
            <button type="button" className="ud-btn ud-btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="button" className="ud-btn ud-btn-primary ud-btn-danger"
              disabled={order.length < 2 || busy}
              title={order.length < 2 ? 'Tick at least one more document' : undefined}
              onClick={() => onConfirm(order)}>
              {busy ? 'Saving…' : `Combine ${order.length} documents`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CombineDocsModal;
